// @ts-strict-ignore
import { useQuery } from "@apollo/client";
import {
  Badge,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DesktopMacIcon from "@material-ui/icons/DesktopMac";
import FastfoodIcon from "@material-ui/icons/Fastfood";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import PersonIcon from "@material-ui/icons/Person";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";
import PlayCircleFilledWhiteIcon from "@material-ui/icons/PlayCircleFilledWhite";
import SearchIcon from "@material-ui/icons/Search";
import ShoppingBagIcon from "@material-ui/icons/ShoppingBasket";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import WhatshotIcon from "@material-ui/icons/Whatshot";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useRef, useState } from "react";

import { SDUI_THEMES_QUERY, STOREFRONT_LAYOUT_QUERY } from "../sduiQueries";
import { SDUISection, SDUITheme, StorefrontLayout } from "../types";

interface SduiPreviewDialogProps {
  open: boolean;
  initialThemeId?: string;
  onClose: () => void;
  themes: SDUITheme[];
}

interface NavEvent {
  time: string;
  source: string;
  target: string;
  type: "category" | "product" | "banner" | "short" | "nav" | "promo" | "drawer";
}

// ---------------------------------------------------------------------------
// Atmospheric Screen Particle Animation Overlay (2026 Quick-Commerce Festivals)
// ---------------------------------------------------------------------------
const AtmosphericEffect: React.FC<{ effect?: string }> = ({ effect }) => {
  if (!effect || effect === "NONE") return null;

  if (effect === "FESTIVAL_DIYAS") {
    return (
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 12 }}>
        <div style={{ position: "absolute", top: 10, right: 18, fontSize: 18, animation: "diyasGlow 2.5s ease-in-out infinite alternate" }}>🪔</div>
        <div style={{ position: "absolute", top: 75, left: 14, fontSize: 14, animation: "diyasGlow 2s ease-in-out infinite alternate 0.5s" }}>✨</div>
        <div style={{ position: "absolute", top: 160, right: 24, fontSize: 16, animation: "diyasFloat 4s ease-in-out infinite alternate" }}>🪔</div>
        <div style={{ position: "absolute", top: 250, left: 18, fontSize: 15, animation: "diyasGlow 3s ease-in-out infinite alternate 1s" }}>✨</div>
        <div style={{ position: "absolute", top: 370, right: 16, fontSize: 17, animation: "diyasFloat 3.5s ease-in-out infinite alternate 0.7s" }}>🪔</div>
        <div style={{ position: "absolute", top: 490, left: 24, fontSize: 13, animation: "diyasGlow 2.2s ease-in-out infinite alternate 0.3s" }}>✨</div>
      </div>
    );
  }

  if (effect === "CONFETTI") {
    return (
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 12 }}>
        <div style={{ position: "absolute", top: -10, left: "20%", fontSize: 15, animation: "confettiFall 4s linear infinite" }}>🎉</div>
        <div style={{ position: "absolute", top: -15, left: "60%", fontSize: 14, animation: "confettiFall 3.2s linear infinite 1s" }}>🎊</div>
        <div style={{ position: "absolute", top: -8, left: "80%", fontSize: 12, animation: "confettiFall 4.5s linear infinite 0.5s" }}>✨</div>
        <div style={{ position: "absolute", top: -12, left: "40%", fontSize: 14, animation: "confettiFall 3.8s linear infinite 1.5s" }}>🎉</div>
      </div>
    );
  }

  if (effect === "FALLING_LEAVES") {
    return (
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 12 }}>
        <div style={{ position: "absolute", top: -10, left: "15%", fontSize: 15, animation: "leavesDrift 5s ease-in-out infinite" }}>🍃</div>
        <div style={{ position: "absolute", top: -15, left: "75%", fontSize: 14, animation: "leavesDrift 4.2s ease-in-out infinite 1.2s" }}>🌿</div>
        <div style={{ position: "absolute", top: -8, left: "45%", fontSize: 13, animation: "leavesDrift 4.8s ease-in-out infinite 0.7s" }}>🍂</div>
      </div>
    );
  }

  if (effect === "GOLDEN_SPARKLES") {
    return (
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 12 }}>
        <div style={{ position: "absolute", top: 15, left: "8%", fontSize: 18, animation: "diyasGlow 2s ease-in-out infinite alternate" }}>🌾</div>
        <div style={{ position: "absolute", top: 65, right: "12%", fontSize: 16, animation: "diyasFloat 3.5s ease-in-out infinite alternate 0.4s" }}>🌼</div>
        <div style={{ position: "absolute", top: 180, left: "86%", fontSize: 15, animation: "diyasGlow 2.3s ease-in-out infinite alternate 1.1s" }}>✨</div>
        <div style={{ position: "absolute", top: 310, left: "6%", fontSize: 16, animation: "diyasFloat 4s ease-in-out infinite alternate 0.8s" }}>🍃</div>
        <div style={{ position: "absolute", top: 440, right: "10%", fontSize: 17, animation: "diyasGlow 2.7s ease-in-out infinite alternate 0.5s" }}>🌾</div>
        <div style={{ position: "absolute", top: 560, left: "14%", fontSize: 16, animation: "diyasFloat 3.2s ease-in-out infinite alternate 1s" }}>🌼</div>
      </div>
    );
  }

  return null;
};

export const SduiPreviewDialog: React.FC<SduiPreviewDialogProps> = ({
  open,
  initialThemeId,
  onClose,
  themes,
}) => {
  const [deviceMode, setDeviceMode] = useState<"mobile" | "web">("mobile");
  const [selectedThemeId, setSelectedThemeId] = useState<string>("");
  const [cartCount, setCartCount] = useState<number>(2);
  const [navLogs, setNavLogs] = useState<NavEvent[]>([]);
  const [activeBottomTab, setActiveBottomTab] = useState<string>("home");

  // 2026 Quick-Commerce Interactive Drawers State
  const [activeDrawer, setActiveDrawer] = useState<"none" | "delivery_slots" | "wallet_topup" | "cart_preview">("none");
  const [selectedSlot, setSelectedSlot] = useState<string>("instant_10");
  const [walletBalance, setWalletBalance] = useState<number>(450);

  const logContainerRef = useRef<HTMLDivElement>(null);

  // Fetch live storefront layout from Saleor backend with forced fresh network fetch
  const { data, refetch: refetchStorefront } = useQuery<{ storefrontLayout: StorefrontLayout }>(
    STOREFRONT_LAYOUT_QUERY,
    {
      variables: { channel: "kisan-basket", platform: deviceMode === "mobile" ? "APP" : "WEB" },
      fetchPolicy: "network-only",
      skip: !open,
    }
  );

  // Directly fetch themes with forced fresh network fetch so any theme edit reflects immediately!
  const { data: themesData, refetch: refetchThemes } = useQuery<{ sduiThemes: SDUITheme[] }>(
    SDUI_THEMES_QUERY,
    {
      fetchPolicy: "network-only",
      skip: !open,
    }
  );

  const allThemes: SDUITheme[] =
    themesData?.sduiThemes && themesData.sduiThemes.length > 0
      ? themesData.sduiThemes
      : themes;

  // Trigger refetch whenever dialog is opened or device mode changes
  useEffect(() => {
    if (open) {
      refetchStorefront();
      refetchThemes();
    }
  }, [open, deviceMode, refetchStorefront, refetchThemes]);

  // Synchronize initialThemeId prop on open or selection
  useEffect(() => {
    if (initialThemeId) {
      setSelectedThemeId(initialThemeId);
    } else if (!selectedThemeId && allThemes.length > 0) {
      const activePub = allThemes.find(t => t.isPublished);
      setSelectedThemeId(activePub?.id || allThemes[0]?.id || "");
    }
  }, [initialThemeId, open, allThemes, selectedThemeId]);

  const layout = data?.storefrontLayout;
  const sections: SDUISection[] = layout?.sections || [];

  // Active theme resolution: explicit selection > layout activeTheme > first theme
  const activeTheme =
    (selectedThemeId ? allThemes.find(t => t.id === selectedThemeId) : null) ||
    layout?.activeTheme ||
    allThemes[0];

  // Parse styling tokens
  let themeTokens = {
    primaryColor: "#15803D",
    secondaryColor: "#F59E0B",
    accentColor: "#10B981",
    surfaceColor: "#FFFFFF",
    backgroundColor: "#F8FAF6",
    textColor: "#1F2937",
    subtextColor: "#6B7280",
    borderRadius: 12,
    fontFamily: "Inter, system-ui, sans-serif",
    headerBg: "linear-gradient(135deg, #15803D 0%, #166534 100%)",
  };

  if (activeTheme?.themeData) {
    try {
      const parsed =
        typeof activeTheme.themeData === "string"
          ? JSON.parse(activeTheme.themeData)
          : activeTheme.themeData;
      themeTokens = { ...themeTokens, ...parsed };
    } catch {}
  }

  // Parse floating elements config
  let floatingConfig = {
    enable_sticky_cart: true,
    cart_badge_text: "10-MIN EXPRESS",
    enable_delivery_bar: true,
    delivery_eta_text: "Delivering in 11 mins to Indiranagar, Bengaluru",
  };

  if (activeTheme?.floatingElementsConfig) {
    try {
      const parsed =
        typeof activeTheme.floatingElementsConfig === "string"
          ? JSON.parse(activeTheme.floatingElementsConfig)
          : activeTheme.floatingElementsConfig;
      floatingConfig = { ...floatingConfig, ...parsed };
    } catch {}
  }

  const logNav = (source: string, target: string, type: NavEvent["type"]) => {
    const event: NavEvent = {
      time: new Date().toLocaleTimeString(),
      source,
      target,
      type,
    };
    setNavLogs(prev => [event, ...prev.slice(0, 39)]);
  };

  const handleAddToCart = (productName: string, price: number) => {
    setCartCount(prev => prev + 1);
    logNav(`Product: Added "${productName}"`, `Cart updated: ${cartCount + 1} items (₹${price})`, "product");
  };

  const handleSectionClick = (section: SDUISection, fallbackLabel: string) => {
    const targetType = section.navTargetType || "NONE";
    const targetId = section.navTargetId || "";

    if (targetType === "DRAWER_DELIVERY_SLOTS") {
      setActiveDrawer("delivery_slots");
      logNav(`Section: ${section.label}`, "⚡ Opened Delivery Slots Bottom Sheet", "drawer");
      return;
    }
    if (targetType === "DRAWER_WALLET_TOPUP") {
      setActiveDrawer("wallet_topup");
      logNav(`Section: ${section.label}`, "💰 Opened Quick Wallet Top-Up Bottom Sheet", "drawer");
      return;
    }
    if (targetType === "DRAWER_CART_PREVIEW") {
      setActiveDrawer("cart_preview");
      logNav(`Section: ${section.label}`, "🛒 Opened Quick Cart Preview Bottom Sheet", "drawer");
      return;
    }
    if (targetType === "SHORTS_REEL") {
      logNav(`Section: ${section.label}`, `🎬 Video Commerce Reel: [${targetId || "Harvest Reels"}]`, "short");
      return;
    }
    if (targetType === "CATEGORY") {
      logNav(`Section: ${section.label}`, `📂 Category View: /category/${targetId || "fresh-veggies"}`, "category");
      return;
    }
    if (targetType === "PRODUCT") {
      logNav(`Section: ${section.label}`, `🥦 Product Details: /product/${targetId || "farm-item"}`, "product");
      return;
    }
    if (targetType === "COLLECTION") {
      logNav(`Section: ${section.label}`, `🏷️ Collection Showcase: /collection/${targetId || "festive-sale"}`, "banner");
      return;
    }

    logNav(`Section: ${section.label}`, fallbackLabel, "nav");
  };

  const parseConfig = (configStr: any) => {
    if (!configStr) return {};
    if (typeof configStr === "object") return configStr;
    try {
      return JSON.parse(configStr);
    } catch {
      return {};
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          height: "92vh",
          maxHeight: 840,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 16,
        },
      }}
    >
      {/* 2026 Blinkit & Zepto CSS Keyframe Animations */}
      <style>{`
        @keyframes diyasGlow {
          0% { transform: scale(0.9) translateY(0); opacity: 0.7; filter: drop-shadow(0 0 4px #F59E0B); }
          100% { transform: scale(1.15) translateY(-5px); opacity: 1; filter: drop-shadow(0 0 10px #F59E0B); }
        }
        @keyframes diyasFloat {
          0% { transform: translateY(0) rotate(-4deg); }
          100% { transform: translateY(-8px) rotate(4deg); }
        }
        @keyframes confettiFall {
          0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(680px) rotate(360deg); opacity: 0.2; }
        }
        @keyframes leavesDrift {
          0% { transform: translateY(-10px) translateX(0) rotate(0deg); opacity: 0.9; }
          50% { transform: translateY(320px) translateX(12px) rotate(180deg); opacity: 0.8; }
          100% { transform: translateY(680px) translateX(-8px) rotate(360deg); opacity: 0.2; }
        }
        @keyframes speedPulse {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(225, 29, 72, 0.45); }
          70% { transform: scale(1.04); box-shadow: 0 0 0 6px rgba(225, 29, 72, 0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(225, 29, 72, 0); }
        }
        @keyframes cartPillBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes shimmerBeam {
          0% { transform: translateX(-150%) skewX(-20deg); }
          100% { transform: translateX(250%) skewX(-20deg); }
        }
        @keyframes neonGlow {
          0%, 100% { box-shadow: 0 0 4px #10B981, inset 0 0 2px #10B981; }
          50% { box-shadow: 0 0 12px #10B981, 0 0 18px rgba(16, 185, 129, 0.35), inset 0 0 3px #10B981; }
        }
        @keyframes attentionWiggle {
          0%, 100% { transform: rotate(0deg) scale(1); }
          15% { transform: rotate(-7deg) scale(1.08); }
          30% { transform: rotate(7deg) scale(1.08); }
          45% { transform: rotate(-4deg) scale(1.04); }
          60% { transform: rotate(0deg) scale(1); }
        }
        @keyframes scooterRide {
          0% { transform: translateX(-80px); opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translateX(360px); opacity: 0; }
        }
      `}</style>

      {/* Header Bar with Device & Theme Switchers */}
      <DialogTitle
        style={{
          padding: "12px 20px",
          borderBottom: "1px solid #E5E7EB",
          flexShrink: 0,
          backgroundColor: "#FFFFFF",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={3}>
            <Text size={5} fontWeight="bold">
              ⚡ 2026 Quick-Commerce Storefront Simulator
            </Text>
            {activeTheme?.animationEffect && activeTheme.animationEffect !== "NONE" && (
              <Chip
                label={`Atmosphere: ${activeTheme.animationEffect}`}
                size="small"
                style={{ backgroundColor: "#FEF3C7", color: "#92400E", fontWeight: 700, fontSize: 10 }}
              />
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={3}>
            {/* Device Switcher */}
            <Box
              display="flex"
              style={{
                backgroundColor: "#F3F4F6",
                borderRadius: 8,
                padding: 3,
              }}
            >
              <Button
                variant={deviceMode === "mobile" ? "primary" : "secondary"}
                size="small"
                onClick={() => setDeviceMode("mobile")}
                style={{ borderRadius: 6, display: "flex", gap: 6, alignItems: "center" }}
              >
                <PhoneIphoneIcon fontSize="small" /> Mobile App
              </Button>
              <Button
                variant={deviceMode === "web" ? "primary" : "secondary"}
                size="small"
                onClick={() => setDeviceMode("web")}
                style={{ borderRadius: 6, display: "flex", gap: 6, alignItems: "center" }}
              >
                <DesktopMacIcon fontSize="small" /> Web Storefront
              </Button>
            </Box>

            {/* Active Theme Selector */}
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" style={{ fontWeight: 600, color: "#4B5563" }}>
                Theme:
              </Typography>
              <Select
                value={selectedThemeId || activeTheme?.id || ""}
                onChange={e => setSelectedThemeId(e.target.value as string)}
                variant="outlined"
                margin="dense"
                style={{ height: 34, minWidth: 230, fontSize: 13, backgroundColor: "#FFF" }}
              >
                {allThemes.map(t => (
                  <MenuItem key={t.id} value={t.id}>
                    🎨 {t.label || t.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        style={{
          padding: 16,
          backgroundColor: "#F8FAFC",
          flex: 1,
          overflow: "hidden",
          display: "flex",
          gap: 16,
          minHeight: 0,
        }}
      >
        {/* ========================================================= */}
        {/* PREVIEW VIEWPORT                                          */}
        {/* ========================================================= */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{ flex: 1, overflow: "hidden", height: "100%", minHeight: 0 }}
        >
          {deviceMode === "mobile" ? (
            // -------------------------------------------------------
            // MOBILE PHONE CONTAINER FRAME
            // -------------------------------------------------------
            <Box
              style={{
                width: 365,
                height: "100%",
                maxHeight: 720,
                backgroundColor: themeTokens.backgroundColor,
                borderRadius: 34,
                boxShadow: "0 12px 35px rgba(0, 0, 0, 0.16)",
                border: "4px solid #1E293B",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                fontFamily: themeTokens.fontFamily,
                flexShrink: 0,
              }}
            >
              {/* Atmospheric Particle Layer */}
              <AtmosphericEffect effect={activeTheme?.animationEffect} />

              {/* Status Bar */}
              <Box
                style={{
                  backgroundColor: "#0F172A",
                  color: "#FFF",
                  padding: "6px 16px 4px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: 10,
                  fontWeight: 600,
                  flexShrink: 0,
                  zIndex: 10,
                }}
              >
                <span>9:41</span>
                <Box style={{ width: 70, height: 10, backgroundColor: "#1E293B", borderRadius: 6 }} />
                <span>5G 􀛨 100%</span>
              </Box>

              {/* Quick-Commerce Delivery Header */}
              <Box
                style={{
                  background: themeTokens.headerBg,
                  color: "#FFFFFF",
                  padding: "10px 14px 12px",
                  borderBottomLeftRadius: 16,
                  borderBottomRightRadius: 16,
                  boxShadow: "0 3px 5px rgba(0, 0, 0, 0.08)",
                  flexShrink: 0,
                  zIndex: 10,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box
                    onClick={() => {
                      setActiveDrawer("delivery_slots");
                      logNav("Top Delivery Header", "Opened Delivery Slot Selector Bottom Sheet", "drawer");
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3 }}>
                        🌱 Kisan Basket
                      </Typography>
                      <span
                        style={{
                          backgroundColor: "#E11D48",
                          color: "#FFF",
                          fontSize: 9,
                          fontWeight: 800,
                          padding: "1px 6px",
                          borderRadius: 10,
                          animation: "speedPulse 2s infinite",
                        }}
                      >
                        10 MINS
                      </span>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} style={{ marginTop: 2 }}>
                      <LocalShippingIcon style={{ fontSize: 12, color: themeTokens.secondaryColor }} />
                      <Typography style={{ fontSize: 10, opacity: 0.95 }}>
                        {selectedSlot === "instant_10" ? "Indiranagar, BLR • 11 mins" : "Morning Slot: 7:30 AM"} ▾
                      </Typography>
                    </Box>
                  </Box>

                  {/* Header Wallet & Cart Triggers */}
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                      onClick={() => {
                        setActiveDrawer("wallet_topup");
                        logNav("Header Wallet Pill", "Opened Kisan Cash Quick Wallet", "drawer");
                      }}
                      style={{
                        backgroundColor: "rgba(255,255,255,0.22)",
                        padding: "4px 8px",
                        borderRadius: 16,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    >
                      <span>₹{walletBalance}</span>
                    </Box>

                    <Box
                      onClick={() => {
                        setActiveDrawer("cart_preview");
                        logNav("Header Cart Icon", `Opening Cart (${cartCount} items)`, "drawer");
                      }}
                      style={{
                        backgroundColor: "rgba(255,255,255,0.22)",
                        padding: "4px 8px",
                        borderRadius: 16,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <ShoppingCartIcon style={{ fontSize: 15 }} />
                      <Typography style={{ fontSize: 11, fontWeight: 700 }}>{cartCount}</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Instant Search Bar */}
                <Box
                  onClick={() => logNav("Search Bar", "Focusing Storefront Global Search", "nav")}
                  style={{
                    marginTop: 8,
                    backgroundColor: "#FFFFFF",
                    borderRadius: themeTokens.borderRadius,
                    padding: "6px 10px",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: "#6B7280",
                    cursor: "pointer",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  }}
                >
                  <SearchIcon style={{ fontSize: 16, color: themeTokens.primaryColor }} />
                  <span style={{ fontSize: 11 }}>Search fresh vegetables, fruits, dairy...</span>
                </Box>
              </Box>

              {/* Scrollable Homepage Body: 95px bottom padding ensures zero overlap with floating cart! */}
              <Box
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "10px 14px 95px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                  minHeight: 0,
                  zIndex: 5,
                }}
              >
                {/* Dynamic Sections Loop with Layout Variants */}
                {sections.map(section => {
                  const cfg = parseConfig(section.config);
                  const variant = section.layoutVariant || "DEFAULT";
                  const effect = section.animationEffect || "NONE";
                  const spotlight = (cfg.spotlight_item || "").toLowerCase();

                  // Layout Variant 1: Quick-Commerce Category Grid Explorer
                  if (variant === "GRID_4X2" || variant === "GRID_2X2" || section.sectionType === "CATEGORY_GRID") {
                    const rawCategories = Array.isArray(cfg.categories) && cfg.categories.length > 0
                      ? cfg.categories
                      : Array.isArray(cfg.items) && cfg.items.length > 0
                      ? cfg.items
                      : [
                          { key: "vegetables", name: "Vegetables", emoji: "🥦", badge: "FRESH" },
                          { key: "fruits", name: "Farm Fruits", emoji: "🍎", badge: "20% OFF" },
                          { key: "dairy", name: "Dairy & Milk", emoji: "🥛", badge: "" },
                          { key: "atta", name: "Organic Atta", emoji: "🌾", badge: "" },
                          { key: "herbs", name: "Fresh Herbs", emoji: "🌿", badge: "HOT" },
                          { key: "oils", name: "Cold Pressed", emoji: "🌻", badge: "" },
                          { key: "dryfruits", name: "Dry Fruits", emoji: "🥜", badge: "" },
                          { key: "festive", name: "Utsav Sweets", emoji: "🪔", badge: "FESTIVE" },
                        ];

                    const categoryList = rawCategories.map((cat: any, idx: number) => ({
                      key: (cat.key || cat.slug || cat.id || `cat_${idx}`).toString(),
                      name: cat.name || cat.title || cat.label || `Category ${idx + 1}`,
                      emoji: cat.emoji || cat.icon || "🥦",
                      badge: cat.badge || cat.tag || "",
                      itemCount: cat.itemCount || cat.subtitle || "",
                      imageUrl: cat.imageUrl || cat.image || null,
                    }));

                    const isHorizontal = variant === "HORIZONTAL_SCROLL";
                    const isGrid2x2 = variant === "GRID_2X2";

                    return (
                      <Box key={section.id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {/* Section Header */}
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Typography style={{ fontSize: 13, fontWeight: 800, color: themeTokens.textColor }}>
                              {cfg.title || section.label}
                            </Typography>
                            {cfg.subtitle && (
                              <Typography style={{ fontSize: 9.5, color: themeTokens.subtextColor || "#64748B", fontWeight: 500 }}>
                                {cfg.subtitle}
                              </Typography>
                            )}
                          </Box>
                          {section.badgeText && (
                            <span
                              style={{
                                backgroundColor: section.badgeColor || themeTokens.primaryColor || "#16a34a",
                                color: "#FFF",
                                fontSize: 9,
                                fontWeight: 800,
                                padding: "2px 8px",
                                borderRadius: 10,
                                flexShrink: 0,
                                animation: effect === "PULSE_BADGE" ? "speedPulse 2s infinite" : "none",
                              }}
                            >
                              {section.badgeText}
                            </span>
                          )}
                        </Box>

                        {/* Optional Animated GIF Banner or Rider Banner */}
                        {cfg.gif_banner_url && (
                          <Box
                            style={{
                              width: "100%",
                              height: 64,
                              borderRadius: 8,
                              overflow: "hidden",
                              position: "relative",
                              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                            }}
                          >
                            <img
                              src={cfg.gif_banner_url}
                              alt="Promotional Animation"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          </Box>
                        )}

                        {/* Dynamic Grid / Horizontal Scroll of Categories */}
                        <Box
                          style={
                            isHorizontal
                              ? { display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }
                              : isGrid2x2
                              ? { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }
                              : { display: "grid", gridTemplateColumns: `repeat(${cfg.columns || 4}, 1fr)`, gap: 8 }
                          }
                        >
                          {categoryList.map((cat, idx) => {
                            const isSpotlight =
                              spotlight === cat.key.toLowerCase() ||
                              spotlight === cat.name.toLowerCase() ||
                              cat.name.toLowerCase().includes(spotlight) ||
                              (effect === "PULSE_BORDER" && (spotlight ? cat.name.toLowerCase().includes(spotlight) : idx === 0)) ||
                              (effect === "BOUNCE_WIGGLE" && (spotlight ? cat.name.toLowerCase().includes(spotlight) : cat.badge === "HOT" || cat.badge === "FRESH")) ||
                              (effect === "SHIMMER_SWEEP" && (spotlight ? cat.name.toLowerCase().includes(spotlight) : idx === 0));

                            return (
                              <Box
                                key={cat.key}
                                onClick={() => handleSectionClick(section, `Tapped Category: ${cat.name}`)}
                                style={{
                                  backgroundColor: themeTokens.surfaceColor,
                                  borderRadius: 10,
                                  padding: "8px 4px 6px",
                                  height: 76,
                                  minWidth: isHorizontal ? 76 : "auto",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  textAlign: "center",
                                  cursor: "pointer",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                  position: "relative",
                                  overflow: "hidden",
                                  border: isSpotlight ? "1.5px solid #10B981" : "1px solid #F1F5F9",
                                  animation: isSpotlight
                                    ? effect === "BOUNCE_WIGGLE"
                                      ? "attentionWiggle 2.5s infinite"
                                      : "neonGlow 2s infinite"
                                    : "none",
                                }}
                              >
                                {/* Shimmer Sweep Beam on Spotlight Card */}
                                {isSpotlight && effect === "SHIMMER_SWEEP" && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "40%",
                                      height: "100%",
                                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
                                      animation: "shimmerBeam 2.5s infinite",
                                      pointerEvents: "none",
                                    }}
                                  />
                                )}

                                {cat.badge && (
                                  <span
                                    style={{
                                      position: "absolute",
                                      top: 2,
                                      right: 2,
                                      backgroundColor: cat.badge === "HOT" ? "#E11D48" : "#059669",
                                      color: "#FFF",
                                      fontSize: 7,
                                      fontWeight: 800,
                                      padding: "1px 4px",
                                      borderRadius: 4,
                                      lineHeight: 1,
                                    }}
                                  >
                                    {cat.badge}
                                  </span>
                                )}
                                <span style={{ fontSize: 22, marginTop: cat.badge ? 4 : 0 }}>{cat.emoji}</span>
                                <Typography
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    marginTop: 3,
                                    lineHeight: 1.15,
                                    maxWidth: "90%",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {cat.name}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  }

                  // Layout Variant 2: Slim Banner Strip (Announcements / Flash Deals)
                  if (variant === "BANNER_STRIP" || section.sectionType === "PROMOTIONAL_STRIP") {
                    return (
                      <Box
                        key={section.id}
                        onClick={() => handleSectionClick(section, `Tapped Announcement Strip: "${cfg.title || section.label}"`)}
                        style={{
                          backgroundColor: cfg.backgroundColor || "#DCFCE7",
                          color: cfg.textColor || "#166534",
                          borderRadius: themeTokens.borderRadius,
                          padding: "8px 12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          fontSize: 11,
                          fontWeight: 700,
                          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5} style={{ maxWidth: "85%" }}>
                          <span
                            style={{
                              backgroundColor: section.badgeColor || "#E11D48",
                              color: "#FFF",
                              padding: "2px 7px",
                              borderRadius: 6,
                              fontSize: 9,
                              fontWeight: 800,
                              flexShrink: 0,
                              animation: effect === "PULSE_BADGE" || effect === "BOUNCE_WIGGLE" ? "speedPulse 2s infinite" : "none",
                            }}
                          >
                            {section.badgeText || cfg.badge || "⚡ 10 MINS"}
                          </span>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {cfg.title || section.label}
                          </span>
                        </Box>
                        <NavigateNextIcon fontSize="small" style={{ flexShrink: 0 }} />
                      </Box>
                    );
                  }

                  // Layout Variant 3: Card Slider / Hero Carousel with 2026 Blinkit/Zepto Banner Animations
                  if (variant === "CARD_SLIDER" || section.sectionType === "CAROUSEL_BANNER") {
                    return (
                      <Box
                        key={section.id}
                        onClick={() => handleSectionClick(section, `Clicked Hero Card Slider -> /collection/${section.navTargetId || "specials"}`)}
                        style={{
                          borderRadius: themeTokens.borderRadius,
                          overflow: "hidden",
                          cursor: "pointer",
                          position: "relative",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                          height: 138,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          padding: "16px 18px",
                          background: `linear-gradient(135deg, ${themeTokens.primaryColor} 0%, ${themeTokens.secondaryColor} 100%)`,
                          color: "#FFF",
                        }}
                      >
                        {/* Shimmer Wave Beam Effect across the whole Banner */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "35%",
                            height: "100%",
                            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
                            animation: "shimmerBeam 3.5s infinite",
                            pointerEvents: "none",
                          }}
                        />

                        {/* Top-Right Badge: Fixed position with max-width protection so it NEVER collides */}
                        {section.badgeText && (
                          <span
                            style={{
                              position: "absolute",
                              top: 10,
                              right: 12,
                              backgroundColor: section.badgeColor || "#F59E0B",
                              color: "#FFF",
                              fontSize: 9,
                              fontWeight: 800,
                              padding: "2px 8px",
                              borderRadius: 6,
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                              zIndex: 2,
                            }}
                          >
                            {section.badgeText}
                          </span>
                        )}

                        {/* Title Box with constrained width so it never touches the badge */}
                        <Box style={{ maxWidth: "70%", zIndex: 2 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.9 }}>
                            {cfg.subtitle || "Utsav Harvest Flash"}
                          </span>
                          <Typography
                            style={{
                              fontSize: 16,
                              fontWeight: 900,
                              marginTop: 2,
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {cfg.title || section.label}
                          </Typography>
                          <Typography style={{ fontSize: 10, opacity: 0.95, marginTop: 4 }}>
                            Direct from Purandar Orchards • 10-15 Mins
                          </Typography>
                        </Box>

                        {/* Decorative Animated Banner Element (Diya Flame or Scooter or Sparkles) */}
                        <Box
                          style={{
                            position: "absolute",
                            right: 14,
                            bottom: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            zIndex: 2,
                          }}
                        >
                          {effect === "BANNER_GIF_RIDER" ? (
                            <span style={{ fontSize: 26, animation: "diyasFloat 2s infinite alternate" }}>🛵💨</span>
                          ) : effect === "FESTIVAL_DIYAS" ? (
                            <span style={{ fontSize: 28, animation: "diyasGlow 2s infinite alternate" }}>🪔</span>
                          ) : (
                            <span style={{ fontSize: 24, animation: "diyasGlow 2.5s infinite alternate" }}>✨</span>
                          )}
                        </Box>

                        {/* Carousel Dots */}
                        <Box display="flex" gap={1} style={{ position: "absolute", bottom: 10, left: 18, zIndex: 2 }}>
                          <Box style={{ width: 16, height: 3, borderRadius: 2, backgroundColor: "#FFF" }} />
                          <Box style={{ width: 6, height: 3, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.4)" }} />
                          <Box style={{ width: 6, height: 3, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.4)" }} />
                        </Box>
                      </Box>
                    );
                  }

                  // Layout Variant 4: Horizontal Scroll Produce Cards
                  if (variant === "HORIZONTAL_SCROLL" || section.sectionType === "PRODUCT_LIST" || section.sectionType === "SEASONAL_PICK") {
                    const rawProducts = Array.isArray(cfg.items) && cfg.items.length > 0
                      ? cfg.items
                      : Array.isArray(cfg.products) && cfg.products.length > 0
                      ? cfg.products
                      : [
                          { key: "palak", name: "Organic Palak", weight: "250g", price: 35, mrp: 50, tag: "10 MINS", icon: "🥬" },
                          { key: "tomatoes", name: "Farm Tomatoes", weight: "1kg", price: 40, mrp: 60, tag: "FRESH", icon: "🍅" },
                          { key: "figs", name: "Purandar Figs", weight: "500g", price: 120, mrp: 160, tag: "HARVEST", icon: "🥭" },
                          { key: "milk", name: "A2 Cow Milk", weight: "1 Litre", price: 85, mrp: 95, tag: "DAILY", icon: "🥛" },
                        ];

                    const productList = rawProducts.map((p: any, idx: number) => ({
                      key: (p.key || p.slug || p.id || `prod_${idx}`).toString(),
                      name: p.name || p.title || `Produce ${idx + 1}`,
                      weight: p.unit || p.weight || "",
                      price: p.price || 0,
                      mrp: p.mrp || p.price || 0,
                      tag: p.tag || p.badge || (p.discount ? p.discount : "FRESH"),
                      icon: p.icon || p.emoji || "🥬",
                    }));

                    return (
                      <Box key={section.id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography style={{ fontSize: 12, fontWeight: 800, color: themeTokens.textColor }}>
                                {cfg.title || section.label}
                              </Typography>
                              {section.badgeText && (
                                <span
                                  style={{
                                    backgroundColor: section.badgeColor || "#059669",
                                    color: "#FFF",
                                    fontSize: 8,
                                    fontWeight: 800,
                                    padding: "1px 5px",
                                    borderRadius: 4,
                                  }}
                                >
                                  {section.badgeText}
                                </span>
                              )}
                            </Box>
                            {cfg.subtitle && (
                              <Typography style={{ fontSize: 9.5, color: themeTokens.subtextColor || "#64748B", fontWeight: 500 }}>
                                {cfg.subtitle}
                              </Typography>
                            )}
                          </Box>
                          <span
                            onClick={() => handleSectionClick(section, `View All: ${cfg.title || section.label}`)}
                            style={{ fontSize: 10, color: themeTokens.primaryColor, fontWeight: 700, cursor: "pointer" }}
                          >
                            View All →
                          </span>
                        </Box>

                        <Box
                          style={{
                            display: "flex",
                            gap: 10,
                            overflowX: "auto",
                            paddingBottom: 4,
                          }}
                        >
                          {productList.map(prod => {
                            const isProductSpotlight =
                              spotlight === prod.key.toLowerCase() ||
                              spotlight === prod.name.toLowerCase() ||
                              prod.name.toLowerCase().includes(spotlight) ||
                              (effect === "PULSE_BORDER" && (spotlight ? prod.name.toLowerCase().includes(spotlight) : prod.key === "palak"));

                            return (
                              <Box
                                key={prod.key}
                                style={{
                                  width: 124,
                                  height: 162,
                                  backgroundColor: themeTokens.surfaceColor,
                                  borderRadius: themeTokens.borderRadius,
                                  padding: "8px 10px 10px",
                                  flexShrink: 0,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "space-between",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                  position: "relative",
                                  overflow: "hidden",
                                  border: isProductSpotlight ? "1.5px solid #10B981" : "1px solid #F1F5F9",
                                  animation: isProductSpotlight ? "neonGlow 2s infinite" : "none",
                                }}
                              >
                                {isProductSpotlight && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "40%",
                                      height: "100%",
                                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
                                      animation: "shimmerBeam 2.5s infinite",
                                      pointerEvents: "none",
                                    }}
                                  />
                                )}

                                <span
                                  style={{
                                    position: "absolute",
                                    top: 6,
                                    left: 6,
                                    backgroundColor: isProductSpotlight ? "#10B981" : "#DCFCE7",
                                    color: isProductSpotlight ? "#FFF" : "#166534",
                                    fontSize: 7,
                                    fontWeight: 800,
                                    padding: "1px 4px",
                                    borderRadius: 3,
                                  }}
                                >
                                  {isProductSpotlight ? "⚡ SPOTLIGHT" : prod.tag}
                                </span>
                                <Box style={{ height: 46, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginTop: 12 }}>
                                  {prod.icon || "🥬"}
                                </Box>
                                <Typography
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    marginTop: 4,
                                    lineHeight: 1.15,
                                    height: 23,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {prod.name}
                                </Typography>
                                <Typography style={{ fontSize: 9, color: "#6B7280" }}>{prod.weight}</Typography>
                                <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginTop: "auto" }}>
                                  <Box>
                                    <span style={{ fontSize: 11, fontWeight: 800, color: "#111827" }}>₹{prod.price}</span>
                                    <span style={{ fontSize: 8, textDecoration: "line-through", color: "#9CA3AF", marginLeft: 3 }}>
                                      ₹{prod.mrp}
                                    </span>
                                  </Box>
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleAddToCart(prod.name, prod.price);
                                    }}
                                    style={{
                                      backgroundColor: "#DCFCE7",
                                      color: "#15803D",
                                      border: "1px solid #86EFAC",
                                      borderRadius: 6,
                                      padding: "2px 8px",
                                      fontSize: 10,
                                      fontWeight: 800,
                                      cursor: "pointer",
                                    }}
                                  >
                                    ADD
                                  </button>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  }

                  // Short Video Reels
                  if (section.sectionType === "SHORT_VIDEO") {
                    const rawReels = Array.isArray(cfg.reels) && cfg.reels.length > 0
                      ? cfg.reels
                      : [
                          { title: "5 AM Fresh Spinach Harvest", views: "24.8K", location: "Saswad" },
                          { title: "Purandar GI Figs from Orchard", views: "42.1K", location: "Purandar" },
                          { title: "Traditional Cold Pressed Oil", views: "18.5K", location: "Kolhapur" },
                          { title: "Pure Gir Cow Vedic Bilona Ghee", views: "35.6K", location: "Pune" },
                        ];

                    return (
                      <Box key={section.id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography style={{ fontSize: 12, fontWeight: 800, color: themeTokens.textColor }}>
                            {cfg.title || "🎬 Farm Stories & Shoppable Reels"}
                          </Typography>
                          <span
                            onClick={() => handleSectionClick(section, "Opened Shoppable Video Feed")}
                            style={{ fontSize: 10, color: themeTokens.primaryColor, fontWeight: 700, cursor: "pointer" }}
                          >
                            Watch All →
                          </span>
                        </Box>
                        <Box display="flex" gap={10} style={{ overflowX: "auto" }}>
                          {rawReels.map((reel: any, idx: number) => (
                            <Box
                              key={idx}
                              onClick={() => handleSectionClick(section, `Playing Video Reel: "${reel.title}"`)}
                              style={{
                                width: 100,
                                height: 140,
                                borderRadius: themeTokens.borderRadius,
                                background: "linear-gradient(180deg, #1E293B 0%, #0F172A 100%)",
                                flexShrink: 0,
                                padding: 8,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                color: "#FFF",
                                cursor: "pointer",
                                position: "relative",
                              }}
                            >
                              <span style={{ fontSize: 8, backgroundColor: "rgba(225,29,72,0.9)", padding: "1px 4px", borderRadius: 3, width: "fit-content" }}>
                                {reel.location || "LIVE FARM"}
                              </span>
                              <Box style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <PlayCircleFilledWhiteIcon style={{ fontSize: 26, opacity: 0.85 }} />
                              </Box>
                              <Box>
                                <Typography style={{ fontSize: 9, fontWeight: 700, lineHeight: 1.1 }}>{reel.title}</Typography>
                                <Typography style={{ fontSize: 7, opacity: 0.8 }}>👁️ {reel.views}</Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  }

                  // Seasonal Farmer Spotlight & Story
                  if (section.sectionType === "SEASONAL_PICK") {
                    const farmerName = cfg.farmerName || "Dnyaneshwar Shinde & Family (Purandar, Pune)";
                    const experience = cfg.experience || "22+ Years Chemical-Free Organic Farming";
                    const produce = cfg.produce || "Purandar GI Figs & Organic Guavas";
                    const tagline = cfg.tagline || "Grown with natural Jeevamrut fertilizers. 100% nutrient-dense farm harvest directly to your kitchen.";
                    const farmLocation = cfg.farmLocation || "Purandar Foothills, Pune";

                    return (
                      <Box
                        key={section.id}
                        onClick={() => handleSectionClick(section, `Tapped Farmer Honor: ${farmerName}`)}
                        style={{
                          backgroundColor: "#FEFCE8",
                          border: "1.5px solid #FDE047",
                          borderRadius: themeTokens.borderRadius,
                          padding: "12px 14px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                          boxShadow: "0 2px 8px rgba(180, 83, 9, 0.08)",
                          cursor: "pointer",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <span style={{ fontSize: 9, backgroundColor: "#B45309", color: "#FFF", fontWeight: 800, padding: "2px 7px", borderRadius: 4 }}>
                            👨‍🌾 {section.badgeText || "FARMER SPOTLIGHT"}
                          </span>
                          <span style={{ fontSize: 10, color: "#854D0E", fontWeight: 700 }}>{farmLocation}</span>
                        </Box>
                        <Box display="flex" gap={2} alignItems="center" style={{ marginTop: 2 }}>
                          <div style={{ width: 44, height: 44, borderRadius: "50%", backgroundColor: "#FEF08A", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: "2px solid #CA8A04" }}>
                            🌾
                          </div>
                          <Box display="flex" flexDirection="column">
                            <Typography style={{ fontSize: 12, fontWeight: 800, color: "#713F12" }}>
                              {farmerName}
                            </Typography>
                            <Typography style={{ fontSize: 9.5, color: "#854D0E", fontWeight: 600 }}>
                              {experience} • {produce}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography style={{ fontSize: 9.5, color: "#A16207", fontStyle: "italic", lineHeight: 1.3 }}>
                          "{tagline}"
                        </Typography>
                      </Box>
                    );
                  }

                  // Default Fallback Section
                  return (
                    <Box
                      key={section.id}
                      onClick={() => handleSectionClick(section, `Tapped Section: ${section.label}`)}
                      style={{
                        backgroundColor: themeTokens.surfaceColor,
                        borderRadius: themeTokens.borderRadius,
                        padding: 10,
                        cursor: "pointer",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      }}
                    >
                      <Typography style={{ fontSize: 11, fontWeight: 700 }}>{section.label}</Typography>
                      <Typography style={{ fontSize: 9, color: "#6B7280" }}>{cfg.subtitle || section.sectionType}</Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* ------------------------------------------------------- */}
              {/* FLOATING STICKY QUICK-COMMERCE CART PILL                */}
              {/* ------------------------------------------------------- */}
              {floatingConfig.enable_sticky_cart && cartCount > 0 && (
                <Box
                  onClick={() => {
                    setActiveDrawer("cart_preview");
                    logNav("Floating Cart Pill", `Opened Quick Checkout Drawer (${cartCount} items)`, "drawer");
                  }}
                  style={{
                    position: "absolute",
                    bottom: 56, // strictly anchored above bottom navigation
                    left: 12,
                    right: 12,
                    height: 44,
                    backgroundColor: themeTokens.primaryColor,
                    color: "#FFFFFF",
                    borderRadius: 14,
                    padding: "0 14px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                    zIndex: 30,
                    animation: "cartPillBounce 2.5s infinite",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                      style={{
                        backgroundColor: "rgba(255,255,255,0.25)",
                        padding: "2px 6px",
                        borderRadius: 6,
                        fontSize: 10,
                        fontWeight: 800,
                      }}
                    >
                      {cartCount} ITEMS
                    </Box>
                    <Box>
                      <div style={{ fontSize: 11, fontWeight: 800 }}>₹{cartCount * 45 + 35}</div>
                      <div style={{ fontSize: 8, opacity: 0.9 }}>{floatingConfig.cart_badge_text || "10-MIN EXPRESS"}</div>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={0.5}>
                    <span style={{ fontSize: 11, fontWeight: 800 }}>VIEW CART</span>
                    <NavigateNextIcon fontSize="small" />
                  </Box>
                </Box>
              )}

              {/* Bottom Navigation Bar */}
              <Box
                style={{
                  backgroundColor: "#FFFFFF",
                  borderTop: "1px solid #E5E7EB",
                  display: "flex",
                  justifyContent: "space-around",
                  padding: "6px 0 8px",
                  flexShrink: 0,
                  zIndex: 20,
                }}
              >
                {[
                  { id: "home", label: "Home", icon: <FastfoodIcon fontSize="small" /> },
                  { id: "categories", label: "Categories", icon: <ViewModuleIcon fontSize="small" /> },
                  { id: "shorts", label: "Shorts", icon: <WhatshotIcon fontSize="small" /> },
                  {
                    id: "cart",
                    label: "Cart",
                    icon: (
                      <Badge badgeContent={cartCount} color="error">
                        <ShoppingBagIcon fontSize="small" />
                      </Badge>
                    ),
                  },
                  { id: "profile", label: "Account", icon: <PersonIcon fontSize="small" /> },
                ].map(tab => {
                  const isActive = activeBottomTab === tab.id;
                  return (
                    <Box
                      key={tab.id}
                      onClick={() => {
                        setActiveBottomTab(tab.id);
                        if (tab.id === "cart") {
                          setActiveDrawer("cart_preview");
                        }
                        logNav("Bottom Tab", `Switched tab to: ${tab.label.toUpperCase()}`, "nav");
                      }}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        cursor: "pointer",
                        color: isActive ? themeTokens.primaryColor : "#9CA3AF",
                        fontSize: 9,
                        fontWeight: isActive ? 700 : 500,
                      }}
                    >
                      {tab.icon}
                      <span style={{ marginTop: 1 }}>{tab.label}</span>
                    </Box>
                  );
                })}
              </Box>

              {/* ------------------------------------------------------- */}
              {/* SLIDE-UP QUICK COMMERCE DRAWERS / BOTTOM SHEETS         */}
              {/* ------------------------------------------------------- */}
              {activeDrawer !== "none" && (
                <>
                  {/* Backdrop */}
                  <div
                    onClick={() => setActiveDrawer("none")}
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      zIndex: 40,
                    }}
                  />

                  {/* Drawer Content */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: "#FFFFFF",
                      borderTopLeftRadius: 20,
                      borderTopRightRadius: 20,
                      boxShadow: "0 -6px 25px rgba(0,0,0,0.25)",
                      zIndex: 50,
                      padding: "14px 16px 18px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                      maxHeight: "75%",
                    }}
                  >
                    {/* Grab Handle Pill */}
                    <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: "#E5E7EB", margin: "0 auto" }} />

                    {/* Drawer 1: Delivery Slot Selector */}
                    {activeDrawer === "delivery_slots" && (
                      <>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                              ⚡ Choose Delivery Slot
                            </Typography>
                            <Typography style={{ fontSize: 10, color: "#6B7280" }}>
                              Purandar Hub Darkstore • Indiranagar, Bengaluru
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => setActiveDrawer("none")}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Box display="flex" flexDirection="column" gap={2}>
                          {[
                            { id: "instant_10", title: "⚡ Instant 10-15 Mins Delivery", desc: "Fastest • Delivery partner ready", badge: "POPULAR" },
                            { id: "morning_harvest", title: "🌅 Morning Slot (7:00 - 9:00 AM)", desc: "Fresh harvest direct from Purandar farm", badge: "FRESH" },
                            { id: "evening_slot", title: "🌆 Evening Slot (6:00 - 8:00 PM)", desc: "Post-work doorstep delivery", badge: "" },
                          ].map(slot => {
                            const isSelected = selectedSlot === slot.id;
                            return (
                              <Box
                                key={slot.id}
                                onClick={() => setSelectedSlot(slot.id)}
                                style={{
                                  border: `1.5px solid ${isSelected ? themeTokens.primaryColor : "#E5E7EB"}`,
                                  backgroundColor: isSelected ? "#F0FDF4" : "#FFFFFF",
                                  borderRadius: 10,
                                  padding: "8px 12px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  cursor: "pointer",
                                }}
                              >
                                <Box>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "#111827" }}>{slot.title}</div>
                                  <div style={{ fontSize: 9, color: "#6B7280" }}>{slot.desc}</div>
                                </Box>
                                {slot.badge && (
                                  <span style={{ backgroundColor: "#15803D", color: "#FFF", fontSize: 8, fontWeight: 800, padding: "1px 5px", borderRadius: 4 }}>
                                    {slot.badge}
                                  </span>
                                )}
                              </Box>
                            );
                          })}
                        </Box>

                        <button
                          onClick={() => {
                            setActiveDrawer("none");
                            logNav("Delivery Slot Drawer", `Confirmed Slot: [${selectedSlot}]`, "drawer");
                          }}
                          style={{
                            backgroundColor: themeTokens.primaryColor,
                            color: "#FFFFFF",
                            border: "none",
                            borderRadius: 10,
                            padding: "10px",
                            fontSize: 12,
                            fontWeight: 800,
                            cursor: "pointer",
                            marginTop: 4,
                          }}
                        >
                          Confirm Delivery Slot
                        </button>
                      </>
                    )}

                    {/* Drawer 2: Kisan Cash Wallet Quick Top-Up */}
                    {activeDrawer === "wallet_topup" && (
                      <>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                              💰 Kisan Cash Quick Wallet
                            </Typography>
                            <Typography style={{ fontSize: 10, color: "#15803D", fontWeight: 700 }}>
                              Current Balance: ₹{walletBalance}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => setActiveDrawer("none")}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Typography style={{ fontSize: 10, color: "#6B7280" }}>
                          Instant 1-Click checkout on all farm orders with zero payment failures.
                        </Typography>

                        <Box display="grid" __gridTemplateColumns="1fr 1fr 1fr" gap={2}>
                          {[
                            { amt: 200, cashback: "+ ₹20 CB" },
                            { amt: 500, cashback: "+ ₹60 CB" },
                            { amt: 1000, cashback: "+ ₹150 CB" },
                          ].map(pill => (
                            <Box
                              key={pill.amt}
                              onClick={() => {
                                setWalletBalance(prev => prev + pill.amt);
                                setActiveDrawer("none");
                                logNav("Wallet Drawer", `Added ₹${pill.amt} (${pill.cashback}) -> New Balance: ₹${walletBalance + pill.amt}`, "drawer");
                              }}
                              style={{
                                border: "1px solid #BBF7D0",
                                backgroundColor: "#F0FDF4",
                                borderRadius: 8,
                                padding: "8px 4px",
                                textAlign: "center",
                                cursor: "pointer",
                              }}
                            >
                              <div style={{ fontSize: 12, fontWeight: 800, color: "#166534" }}>+ ₹{pill.amt}</div>
                              <div style={{ fontSize: 8, color: "#E11D48", fontWeight: 700 }}>{pill.cashback}</div>
                            </Box>
                          ))}
                        </Box>

                        <button
                          onClick={() => {
                            setWalletBalance(prev => prev + 500);
                            setActiveDrawer("none");
                            logNav("Wallet Drawer", `Added ₹500 -> New Balance: ₹${walletBalance + 500}`, "drawer");
                          }}
                          style={{
                            backgroundColor: "#15803D",
                            color: "#FFFFFF",
                            border: "none",
                            borderRadius: 10,
                            padding: "10px",
                            fontSize: 12,
                            fontWeight: 800,
                            cursor: "pointer",
                          }}
                        >
                          Add ₹500 & Claim 10% Cashback
                        </button>
                      </>
                    )}

                    {/* Drawer 3: Quick Cart Preview */}
                    {activeDrawer === "cart_preview" && (
                      <>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                              🛒 Cart Summary ({cartCount} Items)
                            </Typography>
                            <Typography style={{ fontSize: 10, color: "#15803D", fontWeight: 700 }}>
                              ⚡ Delivering in 11 Mins to Indiranagar
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => setActiveDrawer("none")}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Box display="flex" flexDirection="column" gap={2} style={{ maxHeight: 180, overflowY: "auto" }}>
                          {[
                            { name: "Purandar Organic Palak", qty: "250g", price: 35 },
                            { name: "Farm Fresh Tomatoes", qty: "1kg", price: 40 },
                            { name: "Purandar Golden Figs", qty: "500g", price: 120 },
                          ].slice(0, Math.min(cartCount, 3)).map((item, idx) => (
                            <Box key={idx} display="flex" justifyContent="space-between" alignItems="center" style={{ fontSize: 11 }}>
                              <Box>
                                <div style={{ fontWeight: 700 }}>{item.name}</div>
                                <div style={{ fontSize: 9, color: "#6B7280" }}>{item.qty}</div>
                              </Box>
                              <span style={{ fontWeight: 800 }}>₹{item.price}</span>
                            </Box>
                          ))}
                        </Box>

                        <Divider />

                        <Box display="flex" justifyContent="space-between" style={{ fontSize: 11 }}>
                          <span>Delivery Fee:</span>
                          <span style={{ color: "#15803D", fontWeight: 800 }}>FREE (Promo)</span>
                        </Box>

                        <button
                          onClick={() => {
                            setActiveDrawer("none");
                            logNav("Cart Drawer", "Checkout Order Placed Successfully! ETA: 11 mins", "drawer");
                          }}
                          style={{
                            backgroundColor: themeTokens.primaryColor,
                            color: "#FFFFFF",
                            border: "none",
                            borderRadius: 10,
                            padding: "10px",
                            fontSize: 12,
                            fontWeight: 800,
                            cursor: "pointer",
                          }}
                        >
                          Slide to Pay ₹{cartCount * 45 + 35} (10 Mins ETA)
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </Box>
          ) : (
            // -------------------------------------------------------
            // DESKTOP WEB STOREFRONT BROWSER FRAME
            // -------------------------------------------------------
            <Box
              style={{
                width: "100%",
                maxWidth: 920,
                height: "100%",
                maxHeight: 720,
                backgroundColor: themeTokens.backgroundColor,
                borderRadius: 12,
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                border: "1px solid #CBD5E1",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                fontFamily: themeTokens.fontFamily,
                position: "relative",
              }}
            >
              <AtmosphericEffect effect={activeTheme?.animationEffect} />

              {/* Browser Mock Tab & Address Bar */}
              <Box
                style={{
                  backgroundColor: "#E2E8F0",
                  padding: "6px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  borderBottom: "1px solid #CBD5E1",
                  flexShrink: 0,
                  zIndex: 10,
                }}
              >
                <Box display="flex" gap={1}>
                  <Box style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: "#EF4444" }} />
                  <Box style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: "#F59E0B" }} />
                  <Box style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: "#10B981" }} />
                </Box>
                <Box
                  style={{
                    flex: 1,
                    backgroundColor: "#FFF",
                    borderRadius: 4,
                    padding: "3px 10px",
                    fontSize: 11,
                    color: "#475569",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span>🔒 https://kisanbasket.com/storefront?channel=kisan-basket</span>
                </Box>
              </Box>

              {/* Web Storefront Desktop Navigation Header */}
              <Box
                style={{
                  background: themeTokens.headerBg,
                  color: "#FFF",
                  padding: "12px 20px",
                  flexShrink: 0,
                  zIndex: 10,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={3}>
                    <Typography style={{ fontSize: 18, fontWeight: 900, letterSpacing: -0.5 }}>
                      🌱 Kisan Basket
                    </Typography>
                    <Box
                      onClick={() => {
                        setActiveDrawer("delivery_slots");
                        logNav("Web Delivery Header", "Opened Delivery Slot Selector", "drawer");
                      }}
                      style={{
                        backgroundColor: "rgba(255,255,255,0.18)",
                        padding: "4px 10px",
                        borderRadius: 20,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        cursor: "pointer",
                      }}
                    >
                      <LocalShippingIcon style={{ fontSize: 14, color: themeTokens.secondaryColor }} />
                      <span style={{ fontSize: 11, fontWeight: 700 }}>
                        Delivery in 11 Mins to Indiranagar ▾
                      </span>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      onClick={() => {
                        setActiveDrawer("wallet_topup");
                        logNav("Web Wallet Pill", "Opened Kisan Cash Wallet", "drawer");
                      }}
                      style={{
                        backgroundColor: "rgba(255,255,255,0.18)",
                        padding: "5px 12px",
                        borderRadius: 20,
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      💰 ₹{walletBalance}
                    </Box>

                    <Box
                      onClick={() => {
                        setActiveDrawer("cart_preview");
                        logNav("Web Cart", `Opened Cart (${cartCount} items)`, "drawer");
                      }}
                      style={{
                        backgroundColor: "#FFF",
                        color: themeTokens.primaryColor,
                        padding: "5px 14px",
                        borderRadius: 20,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontWeight: 800,
                        fontSize: 12,
                      }}
                    >
                      <ShoppingCartIcon style={{ fontSize: 16 }} />
                      <span>{cartCount} Items</span>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Scrollable Web Storefront Content */}
              <Box
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "16px 24px 80px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  minHeight: 0,
                  zIndex: 5,
                }}
              >
                {/* Hero Banner Billboard with Shimmer Beam */}
                <Box
                  onClick={() => logNav("Web Hero Billboard", "Clicked Utsav Farm Harvest Billboard", "banner")}
                  style={{
                    height: 180,
                    borderRadius: themeTokens.borderRadius,
                    background: `linear-gradient(135deg, ${themeTokens.primaryColor} 0%, ${themeTokens.secondaryColor} 100%)`,
                    color: "#FFF",
                    padding: 24,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "30%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                      animation: "shimmerBeam 3.5s infinite",
                      pointerEvents: "none",
                    }}
                  />
                  <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1, zIndex: 2 }}>
                    Purandar Farm Direct • 2026 Harvest
                  </span>
                  <Typography style={{ fontSize: 24, fontWeight: 900, marginTop: 4, zIndex: 2 }}>
                    Fresh Vegetables & Fruits at Your Doorstep
                  </Typography>
                  <Typography style={{ fontSize: 13, opacity: 0.9, marginTop: 4, zIndex: 2 }}>
                    10-15 Minutes Express Delivery in Bengaluru & Pune
                  </Typography>
                </Box>

                {/* Dynamic Web Category Grid */}
                {(() => {
                  const catSection = sections.find(s => s.sectionType === "CATEGORY_GRID");
                  const catCfg = parseConfig(catSection?.config);
                  const rawWebCats = Array.isArray(catCfg.categories) && catCfg.categories.length > 0
                    ? catCfg.categories
                    : Array.isArray(catCfg.items) && catCfg.items.length > 0
                    ? catCfg.items
                    : [
                        { name: "Vegetables", emoji: "🥦" },
                        { name: "Fruits", emoji: "🍎" },
                        { name: "Dairy", emoji: "🥛" },
                        { name: "Atta & Dal", emoji: "🌾" },
                        { name: "Herbs", emoji: "🌿" },
                        { name: "Oils", emoji: "🌻" },
                        { name: "Dry Fruits", emoji: "🥜" },
                        { name: "Festive", emoji: "🪔" },
                      ];

                  return (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" flexDirection="column">
                          <Typography style={{ fontSize: 16, fontWeight: 800, color: themeTokens.textColor }}>
                            {catCfg.title || catSection?.label || "Explore Fresh Categories"}
                          </Typography>
                          {catCfg.subtitle && (
                            <Typography style={{ fontSize: 11, color: themeTokens.subtextColor || "#64748B" }}>
                              {catCfg.subtitle}
                            </Typography>
                          )}
                        </Box>
                        {catSection?.badgeText && (
                          <span
                            style={{
                              backgroundColor: catSection.badgeColor || themeTokens.primaryColor || "#16a34a",
                              color: "#FFF",
                              fontSize: 10,
                              fontWeight: 800,
                              padding: "3px 10px",
                              borderRadius: 12,
                            }}
                          >
                            {catSection.badgeText}
                          </span>
                        )}
                      </Box>
                      <Box display="grid" __gridTemplateColumns={`repeat(${Math.min(rawWebCats.length, 8)}, 1fr)`} gap={2}>
                        {rawWebCats.slice(0, 8).map((cat: any, idx: number) => {
                          const name = cat.name || cat.title || `Category ${idx + 1}`;
                          const emoji = cat.emoji || cat.icon || "🥦";
                          return (
                            <Box
                              key={idx}
                              onClick={() => logNav("Web Category Grid", `Clicked Category: ${name}`, "category")}
                              style={{
                                backgroundColor: "#FFF",
                                borderRadius: 10,
                                padding: "12px 6px",
                                textAlign: "center",
                                cursor: "pointer",
                                border: "1px solid #E2E8F0",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                              }}
                            >
                              <div style={{ fontSize: 24 }}>{emoji}</div>
                              <div style={{ fontSize: 11, fontWeight: 700, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {name}
                              </div>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}

                {/* Dynamic Web Produce Grid */}
                {(() => {
                  const prodSection = sections.find(s => s.sectionType === "PRODUCT_LIST" || s.sectionType === "SEASONAL_PICK");
                  const prodCfg = parseConfig(prodSection?.config);
                  const rawWebProds = Array.isArray(prodCfg.items) && prodCfg.items.length > 0
                    ? prodCfg.items
                    : Array.isArray(prodCfg.products) && prodCfg.products.length > 0
                    ? prodCfg.products
                    : [
                        { name: "Purandar Organic Palak", weight: "250g", price: 35, icon: "🥬" },
                        { name: "Farm Fresh Tomatoes", weight: "1kg", price: 40, icon: "🍅" },
                        { name: "Purandar Golden Figs", weight: "500g", price: 120, icon: "🥭" },
                        { name: "A2 Gir Cow Milk", weight: "1 Litre", price: 85, icon: "🥛" },
                      ];

                  return (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" flexDirection="column">
                          <Typography style={{ fontSize: 16, fontWeight: 800, color: themeTokens.textColor }}>
                            {prodCfg.title || prodSection?.label || "🌿 Today's Harvest Highlights"}
                          </Typography>
                          {prodCfg.subtitle && (
                            <Typography style={{ fontSize: 11, color: themeTokens.subtextColor || "#64748B" }}>
                              {prodCfg.subtitle}
                            </Typography>
                          )}
                        </Box>
                        {prodSection?.badgeText && (
                          <span
                            style={{
                              backgroundColor: prodSection.badgeColor || "#059669",
                              color: "#FFF",
                              fontSize: 10,
                              fontWeight: 800,
                              padding: "3px 10px",
                              borderRadius: 12,
                            }}
                          >
                            {prodSection.badgeText}
                          </span>
                        )}
                      </Box>
                      <Box display="grid" __gridTemplateColumns="repeat(4, 1fr)" gap={3}>
                        {rawWebProds.slice(0, 4).map((item: any, idx: number) => {
                          const name = item.name || item.title || `Produce ${idx + 1}`;
                          const weight = item.unit || item.weight || "";
                          const price = item.price || 0;
                          const icon = item.icon || item.emoji || "🥬";
                          return (
                            <Box
                              key={idx}
                              style={{
                                backgroundColor: "#FFF",
                                borderRadius: 10,
                                padding: 14,
                                border: "1px solid #E2E8F0",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                              }}
                            >
                              <div style={{ height: 60, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36 }}>
                                {icon}
                              </div>
                              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 8 }}>{name}</div>
                              <div style={{ fontSize: 10, color: "#6B7280" }}>{weight}</div>
                              <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginTop: 10 }}>
                                <span style={{ fontSize: 13, fontWeight: 800 }}>₹{price}</span>
                                <button
                                  onClick={() => handleAddToCart(name, price)}
                                  style={{
                                    backgroundColor: "#DCFCE7",
                                    color: "#15803D",
                                    border: "1px solid #86EFAC",
                                    borderRadius: 6,
                                    padding: "3px 10px",
                                    fontSize: 11,
                                    fontWeight: 800,
                                    cursor: "pointer",
                                  }}
                                >
                                  + ADD
                                </button>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          )}
        </Box>

        {/* ========================================================= */}
        {/* RIGHT SIDEBAR: REAL-TIME NAVIGATION CONSOLE & METRICS     */}
        {/* ========================================================= */}
        <Box
          style={{
            width: 320,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            backgroundColor: "#FFFFFF",
            borderRadius: 12,
            padding: 14,
            border: "1px solid #E5E7EB",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            overflow: "hidden",
            height: "100%",
            minHeight: 0,
          }}
        >
          {/* Active Theme Info Card */}
          <Box
            style={{
              backgroundColor: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: 8,
              padding: 10,
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" style={{ fontWeight: 800, color: "#6B7280", textTransform: "uppercase" }}>
              Active 2026 Theme
            </Typography>
            <Typography variant="subtitle2" style={{ fontWeight: 800, color: themeTokens.primaryColor, marginTop: 2 }}>
              {activeTheme?.label || activeTheme?.name || "Kisan Fresh Default"}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} style={{ marginTop: 4 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: themeTokens.primaryColor,
                  display: "inline-block",
                }}
              />
              <span style={{ fontSize: 10, color: "#4B5563" }}>
                Atmosphere: <b>{activeTheme?.animationEffect || "Clean"}</b>
              </span>
            </Box>
          </Box>

          {/* Quick-Commerce Feature Badges */}
          <Box display="flex" gap={1} flexWrap="wrap" style={{ flexShrink: 0 }}>
            <Chip
              size="small"
              label="⚡ 10-Min Fast Engine"
              style={{ fontSize: 9, height: 20, backgroundColor: "#DCFCE7", color: "#166534", fontWeight: 700 }}
            />
            <Chip
              size="small"
              label="📱 Quick Drawers Active"
              style={{ fontSize: 9, height: 20, backgroundColor: "#EFF6FF", color: "#1E40AF", fontWeight: 700 }}
            />
            <Chip
              size="small"
              label={`${sections.length} SDUI Sections`}
              style={{ fontSize: 9, height: 20, backgroundColor: "#FEF3C7", color: "#92400E", fontWeight: 700 }}
            />
          </Box>

          <Divider style={{ flexShrink: 0 }} />

          {/* Real-Time Navigation Event Log */}
          <Box
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" style={{ flexShrink: 0, marginBottom: 4 }}>
              <Typography variant="caption" style={{ fontWeight: 800, textTransform: "uppercase", color: "#4B5563", fontSize: 10 }}>
                Real-Time Navigation Log ({navLogs.length})
              </Typography>
              {navLogs.length > 0 && (
                <span
                  onClick={() => setNavLogs([])}
                  style={{ fontSize: 10, color: "#EF4444", cursor: "pointer", fontWeight: 700 }}
                >
                  Clear
                </span>
              )}
            </Box>

            {/* Scrollable Log Output */}
            <Box
              ref={logContainerRef}
              style={{
                flex: 1,
                backgroundColor: "#0F172A",
                color: "#38BDF8",
                fontFamily: "monospace",
                fontSize: 10,
                padding: 10,
                borderRadius: 6,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 5,
                minHeight: 0,
              }}
            >
              {navLogs.length === 0 ? (
                <span style={{ color: "#64748B", fontStyle: "italic", lineHeight: 1.4 }}>
                  &gt; Waiting for clicks... Tap categories, hero banners, delivery bar, or wallet in the phone.
                </span>
              ) : (
                navLogs.map((log, index) => (
                  <Box key={index} style={{ borderBottom: "1px solid #1E293B", paddingBottom: 3 }}>
                    <span style={{ color: "#94A3B8" }}>[{log.time}]</span>{" "}
                    <span style={{ color: "#34D399", fontWeight: 600 }}>{log.source}</span>
                    <br />
                    <span style={{ color: "#FCD34D", paddingLeft: 6 }}>↳ {log.target}</span>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SduiPreviewDialog;
